import React, { useEffect, useState } from "react"
import { Button, Card, Collapse, Form, Table } from "react-bootstrap"
import InfiniteScroll from "react-infinite-scroll-component"
import { toast } from "react-toastify"
import recordNotFound from "../../../assets/images/record_not_found.png"
import PageTitleButton from "../../../components/PageTitleButton"
import SearchBox from "../../../components/SearchBox"
import SweetAlerts from "../../../components/SweetAlerts"
import TooltipComponent from "../../../components/Tooltip"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import {
  useHandleUnauthorizedAccess,
  HasModulePermission,
} from "../../../lib/GlobalMethods"
import useDebounce from "../../../lib/hooks/useDebounce"
import BrandSkeleton from "../../../skeleton/settings/BrandSkeleton"
import AddEditBrand from "./AddEditBrand"
import { modulePermission } from "../../../lib/GlobalVariables"

const BrandsManagement = () => {
  const canEdit = HasModulePermission(modulePermission.setting, "edit")
  const canDelete = HasModulePermission(modulePermission.setting, "delete")
  const canAdd = HasModulePermission(modulePermission.setting, "add")

  const [show, setShow] = useState(false)
  const [data, setData] = useState([])
  const [isUpdate, setIsUpdate] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [isDelete, setIsDelete] = useState(false)
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: false,
  })
  const [recordsCount, setRecordsCount] = useState({
    total_records: false,
  })
  const [searchQuery, setSearchQuery] = useState("")
  const [hasMore, setHasMore] = useState(true)
  const [filterObj, setFilterObj] = useState({
    status: false,
  })

  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  const handleShow = () => setShow(true)

  const handleShowAlert = () => setShowAlert(true)

  const handleClose = () => {
    setShow(false)
    setIsUpdate(false)
    setShowAlert(false)
    setIsDelete(false)
  }

  const { handleAccess } = useHandleUnauthorizedAccess()

  const handleStatusChange = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === "Active" ? "Inactive" : "Active"
      const response = await axiosProvider({
        method: "PATCH",
        endpoint: admin.updateBrand + id,
        data: { status: newStatus },
      })
      if (response?.data?.code === 200) {
        toast.success(response.data.message)

        setRecordsCount((prevCounts) => {
          return {
            ...prevCounts,
            total_records: prevCounts.total_records,
            active_records:
              newStatus === "Active"
                ? prevCounts.active_records + 1
                : prevCounts.active_records - 1,
            inactive_records:
              newStatus === "Inactive"
                ? prevCounts.inactive_records + 1
                : prevCounts.inactive_records - 1,
          }
        })

        setData((data) =>
          data.map((user) =>
            user.id === id ? { ...user, status: newStatus } : user
          )
        )
      } else {
        throw new Error(response.data.message)
      }
    } catch (error) {
      toast.error(error.message)
    }
  }

  const fetchData = async (searchQuery) => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.brandList}?page=${pagination.current_page}&search_term=${searchQuery}&status=${filterObj.status}`,
      })

      const fetchedData = response?.data?.data || []

      response?.data?.code === 200 && setPagination(response.data.pagination)
      response?.data?.code === 200 &&
        setRecordsCount(response?.data?.record_counts)
      handleAccess({ code: response?.data?.code })
      setHasMore(true)

      if (
        !fetchedData.length ||
        response.data.pagination.current_page ===
          response.data.pagination.total_pages
      ) {
        setHasMore(false)
      }

      setData((prevData) =>
        pagination.current_page === 1
          ? fetchedData
          : [...prevData, ...fetchedData]
      )

      setLoading(false)
    } catch (error) {
      setLoading(false)
      toast.error(error.message || "An error occurred")
    }
  }

  const fetchMoreData = () => {
    if (pagination.current_page < pagination.total_pages) {
      setPagination((prevState) => ({
        ...prevState,
        current_page: prevState.current_page + 1,
      }))
    } else {
      setHasMore(false)
    }
  }

  useEffect(() => {
    fetchData(debouncedSearchQuery)
  }, [debouncedSearchQuery, filterObj, pagination.current_page])

  const handleDelete = async (id) => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: "DELETE",
        endpoint: admin.deleteBrand + id,
      })

      if (response?.data?.code === 200) {
        toast.success(response.data.message)
        setData((data) => data.filter((user) => user.id !== id))

        setRecordsCount((prevCounts) => ({
          ...prevCounts,
          total_records: prevCounts.total_records - 1,
        }))

        setLoading(false)
      } else {
        setLoading(false)
        throw new Error(response?.data?.message)
      }
    } catch (error) {
      setLoading(false)
      toast.error(error.message)
    }
  }

  const selectBrandStatus = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ]

  const [open, setOpen] = useState(false)
  const filterOnClick = () => {
    setOpen(!open)
  }

  return (
    <>
      {canAdd ? (
        <PageTitleButton
          PagetitleText={"Brands Management"}
          HeaderButtonText={"Add Brand"}
          IconclassName={"plus_black"}
          onClick={handleShow}
          filterShow={true}
          filterClick={filterOnClick}
          open={open}
        />
      ) : (
        <PageTitleButton
          PagetitleText={"Brands Management"}
          onClick={handleShow}
          filterShow={true}
          filterClick={filterOnClick}
          open={open}
        />
      )}
      <div className="details_cards_wrapper mb-3">
        <div className="bg_sea_green details_card_tai">
          <div className="card_titleicon">
            <h3 className="card_title">Total</h3>
          </div>
          <p className="tls_numbers">{recordsCount.total_records || 0}</p>
        </div>
      </div>

      <Collapse in={open}>
        <div id="brand-filter-collapse" className="filters_sticky">
          <Card className="mb-3">
            <Card.Body>
              <div className="d-flex gap-2 align-items-center justify-content-between flex-wrap">
                <div className="d-inline-flex gap-2 align-items-center">
                  <SearchBox
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e?.target?.value)
                      setPagination({ current_page: 1 })
                      setLoading(true)
                    }}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Collapse>
      {!loading ? (
        <Card>
          <Card.Body>
            <InfiniteScroll
              dataLength={data?.length || 10}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={<BrandSkeleton infiniteScroll={true} rows={1} />}
            >
              <Table className="rounded mb-0 table-striped" responsive="md">
                <thead>
                  <tr>
                    <th className="text-center">Logo</th>
                    <th className="text-center">Name</th>
                    {(canEdit || canDelete) && (
                      <th className="text-center">Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data?.length > 0 &&
                    data.map((item, index) => (
                      <tr key={index + 1}>
                        <td className="text-center">
                          <img
                            src={
                              item.brand_img
                                ? process.env.REACT_APP_RESOURCE_URL +
                                  item.brand_img
                                : "https://placehold.co/50x50.png"
                            }
                            alt={item.brand_name}
                            title={item.brand_name}
                            className="brand_img"
                          />
                        </td>
                        <td className="text-center">{item.brand_name}</td>
                        {(canEdit || canDelete) && (
                          <td className="text-center">
                            <div className="d-inline-flex gap-2">
                              {canEdit && (
                                <TooltipComponent
                                  toolplace="top"
                                  tooltipText="Edit"
                                >
                                  <Button
                                    variant="btnedit_icon"
                                    className="btn_icon"
                                    onClick={() => {
                                      handleShow()
                                      setIsUpdate(item)
                                    }}
                                  >
                                    <i className="icon_main edit_pencil"></i>
                                  </Button>
                                </TooltipComponent>
                              )}
                              {canDelete && (
                                <TooltipComponent
                                  toolplace="top"
                                  tooltipText="Delete"
                                >
                                  <Button
                                    variant="btndelete_icon"
                                    className="btn_icon"
                                    onClick={() => {
                                      setShowAlert(true)
                                      setIsUpdate(item)
                                      setIsDelete(true)
                                    }}
                                  >
                                    <i className="icon_main delete_trash"></i>
                                  </Button>
                                </TooltipComponent>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  {data?.length === 0 && (
                    <tr>
                      <td className="text-center py-4" colSpan={4}>
                        <div className="norecord_wrapper">
                          <img src={recordNotFound} alt="No Record Found" />
                          <p className="norecords_title">No Records Found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </InfiniteScroll>
          </Card.Body>
        </Card>
      ) : (
        <BrandSkeleton />
      )}

      <AddEditBrand
        show={show}
        setShow={setShow}
        handleClose={handleClose}
        fetchData={fetchData}
        setIsUpdate={setIsUpdate}
        isUpdate={isUpdate}
        setData={setData}
        setIsDelete={setIsDelete}
        setShowAlert={setShowAlert}
      />

      <SweetAlerts
        showAlert={showAlert}
        loading={loading}
        alertMessage={
          isDelete
            ? "Are you sure you want to Delete this Brand?"
            : `Are you sure you want to ${
                isUpdate.status === "Active" ? "Inactive" : "Active"
              } this Brand?`
        }
        handleClose={handleClose}
        iconClassName={isDelete ? "delete_trash" : "exclamation_circle"}
        handleAlert={() => {
          isDelete
            ? handleDelete(isUpdate.id)
            : handleStatusChange(isUpdate.id, isUpdate.status)
        }}
      />
    </>
  )
}

export default BrandsManagement
